import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Film,
  Upload,
  Search,
  Play,
  Clock,
  Layers,
  Wand2,
  Package,
  Plus,
  ArrowLeft,
  Trash2,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Loader2,
  X,
  Radio,
  BarChart3,
  Pencil,
  Zap,
  Globe,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { adsApi, adPodsApi, vastProvidersApi, trackingApi } from '@/api/client';
import type {
  Ad, AdStatus, AdSegment, AdVariant, AdPod, AdPodStatus,
  VastProvider, VastProviderType, VastProviderStatus, TrackingStats,
  AdTrackingRecord, VastTestResult, VastProviderConfig,
} from '@/types';

// ---- Status Badge ----

function AdStatusBadge({ status }: { status: AdStatus }) {
  const colors: Record<AdStatus, string> = {
    uploaded: 'bg-silver-muted/20 text-silver-muted',
    analyzing: 'bg-status-warning/20 text-status-warning',
    analyzed: 'bg-gold/20 text-gold',
    generating: 'bg-status-warning/20 text-status-warning',
    ready: 'bg-status-healthy/20 text-status-healthy',
    error: 'bg-status-error/20 text-status-error',
  };

  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', colors[status])}>
      {status}
    </span>
  );
}

function PodStatusBadge({ status }: { status: AdPodStatus }) {
  const colors: Record<AdPodStatus, string> = {
    pending: 'bg-silver-muted/20 text-silver-muted',
    filled: 'bg-gold/20 text-gold',
    on_air: 'bg-status-healthy/20 text-status-healthy',
    completed: 'bg-silver-muted/20 text-silver-muted',
  };

  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', colors[status])}>
      {status.replace('_', ' ')}
    </span>
  );
}

// ---- Ad Card ----

function AdCard({ ad, onClick }: { ad: Ad; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="bg-surface border border-surface-elevated rounded-xl p-4 cursor-pointer hover:border-gold/30 transition-colors group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-surface-elevated flex items-center justify-center">
            <Film size={18} className="text-gold" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-silver-bright group-hover:text-gold-light transition-colors">
              {ad.name}
            </h3>
            {ad.advertiser && (
              <p className="text-xs text-silver-muted">{ad.advertiser}</p>
            )}
          </div>
        </div>
        <AdStatusBadge status={ad.status} />
      </div>
      <div className="flex items-center gap-4 text-xs text-silver-muted">
        <span className="flex items-center gap-1">
          <Clock size={12} />
          {(ad.originalDurationMs / 1000).toFixed(1)}s
        </span>
        {ad.width && ad.height && (
          <span>{ad.width}x{ad.height}</span>
        )}
        {ad.codec && <span className="font-mono">{ad.codec}</span>}
      </div>
      <div className="flex justify-end mt-2">
        <ChevronRight size={14} className="text-silver-muted group-hover:text-gold transition-colors" />
      </div>
    </div>
  );
}

// ---- Segment Timeline ----

function SegmentTimeline({ segments, totalDuration }: { segments: AdSegment[]; totalDuration: number }) {
  if (!segments.length) return null;

  const typeColors: Record<string, string> = {
    intro: 'bg-blue-500',
    product_shot: 'bg-gold',
    cta: 'bg-status-healthy',
    transition: 'bg-silver-muted',
    filler: 'bg-silver-muted/50',
    outro: 'bg-purple-500',
    dialogue: 'bg-cyan-500',
    music: 'bg-pink-500',
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 h-8 bg-surface-deep rounded-lg overflow-hidden">
        {segments.map((seg) => {
          const width = Math.max((seg.durationMs / totalDuration) * 100, 1);
          return (
            <div
              key={seg.id}
              className={cn(
                'h-full relative group/seg transition-opacity hover:opacity-80',
                typeColors[seg.segmentType] ?? 'bg-silver-muted'
              )}
              style={{
                width: `${width}%`,
                opacity: 0.3 + seg.importanceScore * 0.7,
              }}
              title={`${seg.segmentType} (${seg.importanceScore.toFixed(2)}) - ${(seg.durationMs / 1000).toFixed(1)}s`}
            >
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover/seg:block bg-surface-elevated text-xs text-silver-bright px-2 py-1 rounded whitespace-nowrap z-10">
                {seg.segmentType} ({(seg.durationMs / 1000).toFixed(1)}s)
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(typeColors).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5">
            <span className={cn('w-2.5 h-2.5 rounded-sm', color)} />
            <span className="text-silver-muted">{type.replace('_', ' ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Variant Row ----

function VariantRow({ variant }: { variant: AdVariant }) {
  const statusIcon = {
    generating: <Loader2 size={14} className="text-status-warning animate-spin" />,
    ready: <CheckCircle2 size={14} className="text-status-healthy" />,
    error: <AlertCircle size={14} className="text-status-error" />,
  };

  return (
    <div className="flex items-center justify-between py-2 px-3 border-b border-surface-elevated last:border-b-0">
      <div className="flex items-center gap-3">
        {statusIcon[variant.status]}
        <span className="text-sm text-silver-bright font-mono">
          {(variant.targetDurationMs / 1000).toFixed(0)}s target
        </span>
        <span className="text-xs text-silver-muted">
          ({(variant.actualDurationMs / 1000).toFixed(1)}s actual)
        </span>
      </div>
      <div className="flex items-center gap-3">
        {variant.qualityScore !== null && (
          <span className={cn(
            'text-xs font-mono',
            variant.qualityScore >= 0.8 ? 'text-status-healthy' :
            variant.qualityScore >= 0.5 ? 'text-status-warning' : 'text-status-error'
          )}>
            Q: {variant.qualityScore.toFixed(2)}
          </span>
        )}
        {variant.segmentsKept && (
          <span className="text-xs text-silver-muted">
            {variant.segmentsKept.length} segments
          </span>
        )}
      </div>
    </div>
  );
}

// ---- Ad Detail View ----

function AdDetail({ adId, onBack }: { adId: number; onBack: () => void }) {
  const queryClient = useQueryClient();
  const [variantDurations, setVariantDurations] = useState('15,30');

  const { data: ad, isLoading } = useQuery({
    queryKey: ['ad', adId],
    queryFn: () => adsApi.get(adId),
  });

  const { data: segments } = useQuery({
    queryKey: ['ad-segments', adId],
    queryFn: () => adsApi.getSegments(adId),
    enabled: ad?.status !== 'uploaded',
  });

  const { data: variants } = useQuery({
    queryKey: ['ad-variants', adId],
    queryFn: () => adsApi.getVariants(adId),
  });

  const analyzeMutation = useMutation({
    mutationFn: () => adsApi.analyze(adId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ad', adId] }),
  });

  const generateMutation = useMutation({
    mutationFn: () => {
      const durations = variantDurations
        .split(',')
        .map((d) => Number(d.trim()) * 1000)
        .filter((d) => d > 0);
      return adsApi.generateVariants(adId, durations);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ad-variants', adId] });
      queryClient.invalidateQueries({ queryKey: ['ad', adId] });
    },
  });

  if (isLoading || !ad) {
    return <div className="text-silver-muted text-sm">Loading ad...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="text-silver-muted hover:text-silver-bright transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-silver-bright">{ad.name}</h2>
          {ad.advertiser && <p className="text-xs text-silver-muted">{ad.advertiser}</p>}
        </div>
        <AdStatusBadge status={ad.status} />
      </div>

      {/* Video Metadata */}
      <div className="bg-surface border border-surface-elevated rounded-xl p-5">
        <h3 className="text-sm font-semibold text-silver-bright mb-4">Video Metadata</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <MetaItem label="Duration" value={`${(ad.originalDurationMs / 1000).toFixed(1)}s`} />
          <MetaItem
            label="Dimensions"
            value={ad.width && ad.height ? `${ad.width}x${ad.height}` : '--'}
          />
          <MetaItem label="FPS" value={ad.fps ? `${ad.fps}` : '--'} />
          <MetaItem label="Codec" value={ad.codec ?? '--'} mono />
        </div>
        {(ad.status === 'uploaded' || ad.status === 'error') && (
          <button
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light disabled:opacity-50 transition-colors"
          >
            <Wand2 size={14} />
            {analyzeMutation.isPending ? 'Analyzing...' : 'Analyze'}
          </button>
        )}
        {ad.status === 'analyzing' && (
          <div className="mt-4 flex items-center gap-2 text-sm text-status-warning">
            <Loader2 size={14} className="animate-spin" />
            Analysis in progress...
          </div>
        )}
        {ad.errorMessage && (
          <p className="mt-4 text-sm text-status-error">{ad.errorMessage}</p>
        )}
      </div>

      {/* Segments */}
      {segments && segments.length > 0 && (
        <div className="bg-surface border border-surface-elevated rounded-xl p-5">
          <h3 className="text-sm font-semibold text-silver-bright mb-4">
            Segment Analysis ({segments.length} segments)
          </h3>
          <SegmentTimeline segments={segments} totalDuration={ad.originalDurationMs} />
        </div>
      )}

      {/* Generate Variants */}
      {(ad.status === 'analyzed' || ad.status === 'ready') && (
        <div className="bg-surface border border-surface-elevated rounded-xl p-5">
          <h3 className="text-sm font-semibold text-silver-bright mb-4">Generate Variants</h3>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs text-silver-muted mb-1.5">
                Target Durations (seconds, comma-separated)
              </label>
              <input
                value={variantDurations}
                onChange={(e) => setVariantDurations(e.target.value)}
                className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright font-mono focus:outline-none focus:border-gold/50"
                placeholder="15,30,60"
              />
            </div>
            <button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {generateMutation.isPending ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </div>
      )}

      {/* Variants */}
      {variants && variants.length > 0 && (
        <div className="bg-surface border border-surface-elevated rounded-xl">
          <div className="px-5 py-4 border-b border-surface-elevated">
            <h3 className="text-sm font-semibold text-silver-bright">
              Variants ({variants.length})
            </h3>
          </div>
          {variants.map((v) => (
            <VariantRow key={v.id} variant={v} />
          ))}
        </div>
      )}

      {/* Ad Pods section */}
      <AdPodsSection />
    </div>
  );
}

// ---- Meta Item ----

function MetaItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-silver-muted mb-1">{label}</div>
      <div className={cn('text-sm text-silver-bright', mono && 'font-mono')}>{value}</div>
    </div>
  );
}

// ---- Ad Pods Section ----

function AdPodsSection() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AdPod | null>(null);
  const [podName, setPodName] = useState('');
  const [podDuration, setPodDuration] = useState('60');
  const [podPosition, setPodPosition] = useState('mid_roll');
  const [podTrigger, setPodTrigger] = useState('manual');

  const { data: pods } = useQuery({
    queryKey: ['adpods'],
    queryFn: adPodsApi.list,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      adPodsApi.create({
        name: podName || undefined,
        durationMs: Number(podDuration) * 1000,
        position: podPosition,
        triggerType: podTrigger,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adpods'] });
      setPodName('');
      setShowCreate(false);
    },
  });

  const fillMutation = useMutation({
    mutationFn: (podId: number) => adPodsApi.fill(podId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adpods'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (podId: number) => adPodsApi.delete(podId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adpods'] }),
  });

  return (
    <div className="bg-surface border border-surface-elevated rounded-xl">
      <div className="flex items-center justify-between px-5 py-4 border-b border-surface-elevated">
        <div className="flex items-center gap-2">
          <Package size={16} className="text-gold" />
          <h3 className="text-sm font-semibold text-silver-bright">Ad Pods</h3>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1.5 text-sm text-gold hover:text-gold-light transition-colors"
        >
          <Plus size={14} />
          Create Pod
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="p-4 bg-surface-deep border-b border-surface-elevated space-y-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-silver-muted mb-1">Name</label>
              <input
                value={podName}
                onChange={(e) => setPodName(e.target.value)}
                className="w-full bg-surface border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                placeholder="Ad Pod Name"
              />
            </div>
            <div>
              <label className="block text-xs text-silver-muted mb-1">Duration (s)</label>
              <input
                type="number"
                value={podDuration}
                onChange={(e) => setPodDuration(e.target.value)}
                required
                min={1}
                className="w-full bg-surface border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
              />
            </div>
            <div>
              <label className="block text-xs text-silver-muted mb-1">Position</label>
              <select
                value={podPosition}
                onChange={(e) => setPodPosition(e.target.value)}
                className="w-full bg-surface border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
              >
                <option value="pre_roll">Pre-roll</option>
                <option value="mid_roll">Mid-roll</option>
                <option value="post_roll">Post-roll</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-silver-muted mb-1">Trigger Type</label>
              <select
                value={podTrigger}
                onChange={(e) => setPodTrigger(e.target.value)}
                className="w-full bg-surface border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
              >
                <option value="manual">Manual</option>
                <option value="scte35">SCTE-35</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="px-3 py-1.5 text-sm text-silver-muted hover:text-silver-bright transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-1.5 bg-gold text-surface-deep text-sm font-medium rounded hover:bg-gold-light disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {/* Pod list */}
      {pods && pods.length > 0 ? (
        pods.map((pod) => (
          <div
            key={pod.id}
            className="flex items-center justify-between py-3 px-5 border-b border-surface-elevated last:border-b-0"
          >
            <div className="flex items-center gap-3">
              <Package size={14} className="text-silver-muted" />
              <div>
                <span className="text-sm text-silver-bright">
                  {pod.name ?? `Pod #${pod.id}`}
                </span>
                <span className="text-xs text-silver-muted ml-2">
                  {(pod.durationMs / 1000).toFixed(0)}s
                </span>
                {pod.position && (
                  <span className="text-xs text-silver-muted ml-2">{pod.position.replace('_', ' ')}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-silver-muted">
                {pod.adVariantIds.length} variants
              </span>
              <PodStatusBadge status={pod.status} />
              {pod.status === 'pending' && (
                <button
                  onClick={() => fillMutation.mutate(pod.id)}
                  disabled={fillMutation.isPending}
                  className="text-xs text-gold hover:text-gold-light transition-colors"
                >
                  Fill
                </button>
              )}
              <button
                onClick={() => setConfirmDelete(pod)}
                className="text-silver-muted hover:text-status-error transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))
      ) : (
        <div className="p-6 text-center text-silver-muted text-sm">No ad pods</div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-sm mx-4 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-silver-bright">Delete Ad Pod</h3>
            <p className="text-sm text-silver-muted">
              Are you sure you want to delete <span className="text-silver-bright font-medium">{confirmDelete.name ?? `Pod #${confirmDelete.id}`}</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteMutation.mutate(confirmDelete.id);
                  setConfirmDelete(null);
                }}
                className="px-4 py-2 bg-status-error text-white text-sm font-medium rounded-lg hover:bg-status-error/80 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Provider Type Badge ----

function ProviderTypeBadge({ type }: { type: VastProviderType }) {
  const labels: Record<VastProviderType, string> = {
    google_ad_manager: 'GAM',
    freewheel: 'FreeWheel',
    springserve: 'SpringServe',
    custom: 'Custom',
  };
  const colors: Record<VastProviderType, string> = {
    google_ad_manager: 'bg-blue-500/20 text-blue-400',
    freewheel: 'bg-purple-500/20 text-purple-400',
    springserve: 'bg-cyan-500/20 text-cyan-400',
    custom: 'bg-silver-muted/20 text-silver-muted',
  };

  return (
    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', colors[type])}>
      {labels[type]}
    </span>
  );
}

function ProviderStatusDot({ status }: { status: VastProviderStatus }) {
  const colors: Record<VastProviderStatus, string> = {
    active: 'bg-status-healthy',
    inactive: 'bg-silver-muted',
    error: 'bg-status-error',
  };

  return (
    <span className="flex items-center gap-1.5 text-xs text-silver-muted">
      <span className={cn('w-2 h-2 rounded-full', colors[status])} />
      {status}
    </span>
  );
}

// ---- VAST Providers Tab ----

function VastProvidersTab() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<VastProvider | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; result?: VastTestResult; error?: string; loading: boolean } | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<VastProviderType>('custom');
  const [formUrl, setFormUrl] = useState('');
  const [formPriority, setFormPriority] = useState('0');
  const [formTimeout, setFormTimeout] = useState('5000');
  const [formRetries, setFormRetries] = useState('1');
  // Provider-specific config
  const [formNetworkId, setFormNetworkId] = useState('');
  const [formSiteId, setFormSiteId] = useState('');
  const [formProfileId, setFormProfileId] = useState('');
  const [formAdUnitPath, setFormAdUnitPath] = useState('');

  const { data: providers, isLoading } = useQuery({
    queryKey: ['vast-providers'],
    queryFn: vastProvidersApi.list,
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const config: VastProviderConfig = {
        timeoutMs: Number(formTimeout) || 5000,
        maxRetries: Number(formRetries) || 1,
      };
      if (formNetworkId) config.networkId = formNetworkId;
      if (formSiteId) config.siteId = formSiteId;
      if (formProfileId) config.profileId = formProfileId;
      if (formAdUnitPath) config.adUnitPath = formAdUnitPath;

      return vastProvidersApi.create({
        name: formName,
        providerType: formType,
        vastTagUrl: formUrl,
        priority: Number(formPriority) || 0,
        config,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vast-providers'] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof vastProvidersApi.update>[1] }) =>
      vastProvidersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vast-providers'] });
      setEditingId(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => vastProvidersApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vast-providers'] }),
  });

  function resetForm() {
    setFormName('');
    setFormType('custom');
    setFormUrl('');
    setFormPriority('0');
    setFormTimeout('5000');
    setFormRetries('1');
    setFormNetworkId('');
    setFormSiteId('');
    setFormProfileId('');
    setFormAdUnitPath('');
    setShowCreate(false);
  }

  function startEdit(p: VastProvider) {
    setEditingId(p.id);
    setFormName(p.name);
    setFormType(p.provider_type);
    setFormUrl(p.vast_tag_url);
    setFormPriority(String(p.priority));
    setFormTimeout(String(p.config.timeoutMs ?? 5000));
    setFormRetries(String(p.config.maxRetries ?? 1));
    setFormNetworkId(p.config.networkId ?? '');
    setFormSiteId(p.config.siteId ?? '');
    setFormProfileId(p.config.profileId ?? '');
    setFormAdUnitPath(p.config.adUnitPath ?? '');
    setShowCreate(true);
  }

  async function handleTest(id: string) {
    setTestResult({ id, loading: true });
    try {
      const result = await vastProvidersApi.test(id);
      setTestResult({ id, result, loading: false });
    } catch (err) {
      setTestResult({ id, error: err instanceof Error ? err.message : String(err), loading: false });
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editingId) {
      const config: VastProviderConfig = {
        timeoutMs: Number(formTimeout) || 5000,
        maxRetries: Number(formRetries) || 1,
      };
      if (formNetworkId) config.networkId = formNetworkId;
      if (formSiteId) config.siteId = formSiteId;
      if (formProfileId) config.profileId = formProfileId;
      if (formAdUnitPath) config.adUnitPath = formAdUnitPath;

      updateMutation.mutate({
        id: editingId,
        data: {
          name: formName,
          providerType: formType,
          vastTagUrl: formUrl,
          priority: Number(formPriority) || 0,
          config,
        },
      });
    } else {
      createMutation.mutate();
    }
  }

  return (
    <div className="space-y-4">
      {/* Add Provider button */}
      <div className="flex justify-end">
        <button
          onClick={() => { resetForm(); setEditingId(null); setShowCreate(!showCreate); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light transition-colors"
        >
          <Plus size={14} />
          Add Provider
        </button>
      </div>

      {/* Create / Edit form */}
      {showCreate && (
        <form onSubmit={handleSubmit} className="bg-surface border border-surface-elevated rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-silver-bright">
            {editingId ? 'Edit Provider' : 'New VAST Provider'}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-silver-muted mb-1">Name</label>
              <input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
                className="w-full bg-surface-deep border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                placeholder="My Ad Provider"
              />
            </div>
            <div>
              <label className="block text-xs text-silver-muted mb-1">Provider Type</label>
              <select
                value={formType}
                onChange={(e) => setFormType(e.target.value as VastProviderType)}
                className="w-full bg-surface-deep border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
              >
                <option value="google_ad_manager">Google Ad Manager</option>
                <option value="freewheel">FreeWheel</option>
                <option value="springserve">SpringServe</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-silver-muted mb-1">VAST Tag URL</label>
              <input
                value={formUrl}
                onChange={(e) => setFormUrl(e.target.value)}
                required
                className="w-full bg-surface-deep border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright font-mono focus:outline-none focus:border-gold/50"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-xs text-silver-muted mb-1">Priority</label>
              <input
                type="number"
                value={formPriority}
                onChange={(e) => setFormPriority(e.target.value)}
                className="w-full bg-surface-deep border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
              />
            </div>
            <div>
              <label className="block text-xs text-silver-muted mb-1">Timeout (ms)</label>
              <input
                type="number"
                value={formTimeout}
                onChange={(e) => setFormTimeout(e.target.value)}
                className="w-full bg-surface-deep border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
              />
            </div>

            {/* Provider-specific fields */}
            {(formType === 'google_ad_manager') && (
              <>
                <div>
                  <label className="block text-xs text-silver-muted mb-1">Ad Unit Path</label>
                  <input
                    value={formAdUnitPath}
                    onChange={(e) => setFormAdUnitPath(e.target.value)}
                    className="w-full bg-surface-deep border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright font-mono focus:outline-none focus:border-gold/50"
                    placeholder="/networkId/adUnit"
                  />
                </div>
                <div>
                  <label className="block text-xs text-silver-muted mb-1">Network ID</label>
                  <input
                    value={formNetworkId}
                    onChange={(e) => setFormNetworkId(e.target.value)}
                    className="w-full bg-surface-deep border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                  />
                </div>
              </>
            )}
            {(formType === 'freewheel') && (
              <>
                <div>
                  <label className="block text-xs text-silver-muted mb-1">Network ID</label>
                  <input
                    value={formNetworkId}
                    onChange={(e) => setFormNetworkId(e.target.value)}
                    className="w-full bg-surface-deep border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-silver-muted mb-1">Site ID (csid)</label>
                  <input
                    value={formSiteId}
                    onChange={(e) => setFormSiteId(e.target.value)}
                    className="w-full bg-surface-deep border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-silver-muted mb-1">Profile ID</label>
                  <input
                    value={formProfileId}
                    onChange={(e) => setFormProfileId(e.target.value)}
                    className="w-full bg-surface-deep border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={resetForm}
              className="px-3 py-1.5 text-sm text-silver-muted hover:text-silver-bright transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-1.5 bg-gold text-surface-deep text-sm font-medium rounded hover:bg-gold-light disabled:opacity-50 transition-colors"
            >
              {editingId
                ? (updateMutation.isPending ? 'Saving...' : 'Save')
                : (createMutation.isPending ? 'Creating...' : 'Create')}
            </button>
          </div>
        </form>
      )}

      {/* Provider list */}
      {isLoading && (
        <div className="text-center py-8 text-silver-muted text-sm">Loading providers...</div>
      )}

      {providers && providers.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <Globe size={40} className="mx-auto text-silver-muted/30" />
          <p className="text-silver-muted">No VAST providers configured</p>
          <p className="text-xs text-silver-muted">Add a provider to enable server-side ad insertion from external ad servers</p>
        </div>
      )}

      {providers && providers.map((p) => (
        <div key={p.id} className="bg-surface border border-surface-elevated rounded-xl p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-surface-elevated flex items-center justify-center">
                <Radio size={18} className="text-gold" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-silver-bright">{p.name}</h4>
                <div className="flex items-center gap-2 mt-0.5">
                  <ProviderTypeBadge type={p.provider_type} />
                  <ProviderStatusDot status={p.status} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleTest(p.id)}
                disabled={testResult?.id === p.id && testResult.loading}
                className="flex items-center gap-1 px-2.5 py-1 text-xs text-gold hover:text-gold-light border border-gold/30 rounded transition-colors disabled:opacity-50"
              >
                {testResult?.id === p.id && testResult.loading
                  ? <Loader2 size={12} className="animate-spin" />
                  : <Zap size={12} />
                }
                Test
              </button>
              <button
                onClick={() => startEdit(p)}
                className="text-silver-muted hover:text-silver-bright transition-colors"
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => setConfirmDelete(p)}
                className="text-silver-muted hover:text-status-error transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs text-silver-muted">
            <span>Priority: {p.priority}</span>
            <span>Requests: {p.request_count}</span>
            <span>Errors: {p.error_count}</span>
            {p.last_request_at && (
              <span>Last request: {new Date(p.last_request_at).toLocaleString()}</span>
            )}
          </div>

          {p.last_error && (
            <div className="mt-2 text-xs text-status-error bg-status-error/10 rounded px-2.5 py-1.5">
              {p.last_error}
            </div>
          )}

          {/* Test result */}
          {testResult?.id === p.id && !testResult.loading && (
            <div className="mt-3 bg-surface-deep rounded-lg p-3">
              {testResult.error ? (
                <div className="text-xs text-status-error">{testResult.error}</div>
              ) : testResult.result && (
                <div className="space-y-2">
                  <div className="text-xs text-status-healthy">
                    Found {testResult.result.creativeCount} creative{testResult.result.creativeCount !== 1 ? 's' : ''}
                  </div>
                  {testResult.result.creatives.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs text-silver-muted">
                      <span className="text-silver-bright">{c.title || 'Untitled'}</span>
                      <span>{(c.durationMs / 1000).toFixed(1)}s</span>
                      <span>{c.width}x{c.height}</span>
                      <span>{c.trackingEventCount} tracking events</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-sm mx-4 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-silver-bright">Delete Provider</h3>
            <p className="text-sm text-silver-muted">
              Are you sure you want to delete <span className="text-silver-bright font-medium">{confirmDelete.name}</span>? This will also remove all cached creatives.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteMutation.mutate(confirmDelete.id);
                  setConfirmDelete(null);
                }}
                className="px-4 py-2 bg-status-error text-white text-sm font-medium rounded-lg hover:bg-status-error/80 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Tracking Tab ----

function TrackingTab() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['tracking-stats'],
    queryFn: trackingApi.getStats,
  });

  return (
    <div className="space-y-6">
      {/* Stats overview */}
      {statsLoading && (
        <div className="text-center py-8 text-silver-muted text-sm">Loading tracking stats...</div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Total Impressions" value={stats.totals.total_impressions} />
            <StatCard label="Completions" value={stats.totals.total_completions} />
            <StatCard
              label="Completion Rate"
              value={
                Number(stats.totals.total_impressions) > 0
                  ? `${((Number(stats.totals.total_completions) / Number(stats.totals.total_impressions)) * 100).toFixed(1)}%`
                  : '--'
              }
            />
            <StatCard label="Errors" value={stats.totals.total_errors} error />
          </div>

          {/* By provider */}
          {stats.byProvider.length > 0 && (
            <div className="bg-surface border border-surface-elevated rounded-xl">
              <div className="px-5 py-4 border-b border-surface-elevated">
                <h3 className="text-sm font-semibold text-silver-bright">Tracking by Provider</h3>
              </div>
              <div className="divide-y divide-surface-elevated">
                {stats.byProvider.map((row) => (
                  <div key={row.provider_id ?? 'unknown'} className="flex items-center justify-between px-5 py-3">
                    <span className="text-sm text-silver-bright">
                      {row.provider_name ?? 'Unknown Provider'}
                    </span>
                    <div className="flex items-center gap-4 text-xs text-silver-muted">
                      <span>{row.impressions} impressions</span>
                      <span>{row.completions} completes</span>
                      <span className={Number(row.errors) > 0 ? 'text-status-error' : ''}>
                        {row.errors} errors
                      </span>
                      <span className="font-mono">{row.total_events} total</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.byProvider.length === 0 && (
            <div className="text-center py-16 space-y-3">
              <BarChart3 size={40} className="mx-auto text-silver-muted/30" />
              <p className="text-silver-muted">No tracking events recorded yet</p>
              <p className="text-xs text-silver-muted">Events will appear here once VAST ads are served and tracked</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, error }: { label: string; value: string; error?: boolean }) {
  return (
    <div className="bg-surface border border-surface-elevated rounded-xl p-4">
      <div className="text-xs text-silver-muted mb-1">{label}</div>
      <div className={cn('text-2xl font-bold', error ? 'text-status-error' : 'text-silver-bright')}>
        {value}
      </div>
    </div>
  );
}

// ---- Tab type ----

type AdsTab = 'creatives' | 'pods' | 'vast-providers' | 'tracking';

// ---- Creatives Tab (original ad list) ----

function CreativesTab({
  selectedAdId,
  setSelectedAdId,
}: {
  selectedAdId: number | null;
  setSelectedAdId: (id: number | null) => void;
}) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: ads, isLoading } = useQuery({
    queryKey: ['ads'],
    queryFn: adsApi.list,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => adsApi.upload(file, { name: file.name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
      e.target.value = '';
    }
  }

  const filteredAds = ads?.filter(
    (ad) =>
      ad.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ad.advertiser?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (selectedAdId !== null) {
    return <AdDetail adId={selectedAdId} onBack={() => setSelectedAdId(null)} />;
  }

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-silver-muted" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-surface border border-surface-elevated rounded-lg pl-10 pr-4 py-2.5 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
            placeholder="Search ads by name or advertiser..."
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-silver-muted hover:text-silver-bright"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light disabled:opacity-50 transition-colors"
          >
            <Upload size={16} />
            {uploadMutation.isPending ? 'Uploading...' : 'Upload Ad'}
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading && (
        <div className="text-center py-12 text-silver-muted text-sm">Loading ads...</div>
      )}

      {filteredAds && filteredAds.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <Film size={40} className="mx-auto text-silver-muted/30" />
          <p className="text-silver-muted">
            {searchQuery ? 'No ads match your search' : 'No ads uploaded yet'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-gold text-sm hover:text-gold-light transition-colors"
            >
              Upload your first ad
            </button>
          )}
        </div>
      )}

      {filteredAds && filteredAds.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAds.map((ad) => (
            <AdCard key={ad.id} ad={ad} onClick={() => setSelectedAdId(ad.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Main Ads Page ----

export default function Ads() {
  const [activeTab, setActiveTab] = useState<AdsTab>('creatives');
  const [selectedAdId, setSelectedAdId] = useState<number | null>(null);

  const tabs: { id: AdsTab; label: string; icon: React.ReactNode }[] = [
    { id: 'creatives', label: 'Creatives', icon: <Film size={14} /> },
    { id: 'pods', label: 'Ad Pods', icon: <Package size={14} /> },
    { id: 'vast-providers', label: 'VAST Providers', icon: <Radio size={14} /> },
    { id: 'tracking', label: 'Tracking', icon: <BarChart3 size={14} /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-silver-bright">Ad Library</h2>
        <p className="text-sm text-silver-muted mt-1">
          Manage, analyze, and generate ad variants
        </p>
      </div>

      {/* Tab navigation */}
      <div className="flex items-center gap-1 border-b border-surface-elevated">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSelectedAdId(null); }}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeTab === tab.id
                ? 'text-gold border-gold'
                : 'text-silver-muted border-transparent hover:text-silver-bright hover:border-silver-muted/30'
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'creatives' && (
        <CreativesTab selectedAdId={selectedAdId} setSelectedAdId={setSelectedAdId} />
      )}
      {activeTab === 'pods' && <AdPodsSection />}
      {activeTab === 'vast-providers' && <VastProvidersTab />}
      {activeTab === 'tracking' && <TrackingTab />}
    </div>
  );
}
